import { useState, useEffect } from "react";
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, deleteField } from "firebase/firestore";
import { db } from "../../../firebase";
import { F, D } from "../../../styles/palette";
import { DC, Bdg } from "../../shared";
import { logAudit } from "../../../utils/auditTrail";

export default function AdminDoctors({ user }) {
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const loadDoctors = async () => {
    setLoading(true);
    try {
      const [snap1, snap2] = await Promise.all([
        getDocs(query(collection(db, "users"), where("role", "==", "doctor"), where("approved", "==", false))),
        getDocs(query(collection(db, "users"), where("role", "==", "doctor"), where("approved", "==", true))),
      ]);
      setPending(snap1.docs.map(d => ({ id: d.id, ...d.data() })));
      setAll(snap2.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadDoctors(); }, []);

  const approve = async (docId, email) => {
    await updateDoc(doc(db, "users", docId), { approved: true, approvedBy: user.uid, approvedAt: serverTimestamp() });
    await logAudit("aprobar_medico", "user", docId, user.uid, user.email, `Aprobado: ${email}`);
    loadDoctors();
  };

  const revoke = async (docId, email) => {
    await updateDoc(doc(db, "users", docId), { approved: false, revokedBy: user.uid, revokedAt: serverTimestamp() });
    await logAudit("revocar_medico", "user", docId, user.uid, user.email, `Revocado: ${email}`);
    loadDoctors();
  };

  const changeRole = async (docId, email, newRole) => {
    setConfirming(null);
    await updateDoc(doc(db, "users", docId), { role: newRole });
    await logAudit("cambiar_rol", "user", docId, user.uid, user.email, `Rol cambiado a ${newRole}: ${email}`);
    loadDoctors();
  };

  const resetTOTP = async (docId, email) => {
    setConfirming(null);
    await updateDoc(doc(db, "users", docId), { totpEnabled: false, totpSecret: deleteField() });
    await logAudit("reset_totp", "user", docId, user.uid, user.email, `TOTP reseteado: ${email}`);
    loadDoctors();
  };

  const revokeDevices = async (docId, email) => {
    setConfirming(null);
    await updateDoc(doc(db, "users", docId), { devices: [] });
    await logAudit("revocar_dispositivos", "user", docId, user.uid, user.email, `Dispositivos revocados: ${email}`);
    loadDoctors();
  };

  const btnStyle = (active) => ({
    padding: "8px 16px", borderRadius: 8,
    border: `2px solid ${active ? D.ac : "rgba(100,116,139,0.12)"}`,
    background: active ? "rgba(45,212,191,0.1)" : "transparent",
    color: active ? D.ac : "#64748b",
    fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: F.b,
  });

  const actionBtn = (label, color, onClick) => (
    <button onClick={onClick} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${color}30`, background: "transparent", color, fontSize: 11, cursor: "pointer", fontFamily: F.b, fontWeight: 600 }}>
      {label}
    </button>
  );

  const DoctorCard = ({ d, showActions }) => {
    const isExp = expanded === d.id;
    return (
      <DC key={d.id} accent={d.approved ? "#16a34a" : "#ca8a04"}>
        <div onClick={() => setExpanded(isExp ? null : d.id)} style={{ cursor: "pointer" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h4 style={{ margin: "0 0 4px", color: "#e2e8f0", fontSize: 14, fontFamily: F.b }}>{d.name}</h4>
              <p style={{ margin: "0 0 2px", color: "#64748b", fontSize: 12 }}>{d.email}</p>
              {d.registroMedico && <p style={{ margin: 0, color: "#94a3b8", fontSize: 11, fontFamily: F.m }}>RM: {d.registroMedico}</p>}
            </div>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {d.totpEnabled && <Bdg t="2FA" c="#16a34a" />}
              {d.role === "admin" && <Bdg t="Admin" c="#dc2626" />}
              <Bdg t={d.approved ? "Aprobado" : "Pendiente"} c={d.approved ? "#16a34a" : "#ca8a04"} />
            </div>
          </div>
        </div>

        {isExp && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${D.bdr}`, paddingTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#64748b" }}>Registro: <span style={{ color: "#94a3b8" }}>{d.createdAt?.toDate?.()?.toLocaleDateString("es-CO") || "N/A"}</span></div>
              <div style={{ fontSize: 11, color: "#64748b" }}>TOTP: <span style={{ color: d.totpEnabled ? "#16a34a" : "#94a3b8" }}>{d.totpEnabled ? "Activo" : "Inactivo"}</span></div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Dispositivos: <span style={{ color: "#94a3b8" }}>{d.devices?.length || 0}</span></div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Rol: <span style={{ color: "#94a3b8" }}>{d.role}</span></div>
            </div>

            {showActions && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {!d.approved && actionBtn("Aprobar", "#16a34a", () => approve(d.id, d.email))}
                {d.approved && actionBtn("Revocar acceso", "#dc2626", () => revoke(d.id, d.email))}
                {d.totpEnabled && actionBtn("Reset TOTP", "#ca8a04", () => setConfirming({ type: "totp", id: d.id, email: d.email }))}
                {d.devices?.length > 0 && actionBtn("Revocar dispositivos", "#ea580c", () => setConfirming({ type: "devices", id: d.id, email: d.email }))}
                {d.role === "doctor" && actionBtn("Promover a Admin", "#7c3aed", () => setConfirming({ type: "role", id: d.id, email: d.email, newRole: "admin" }))}
                {d.role === "admin" && d.id !== user.uid && actionBtn("Degradar a Doctor", "#64748b", () => setConfirming({ type: "role", id: d.id, email: d.email, newRole: "doctor" }))}
              </div>
            )}
          </div>
        )}
      </DC>
    );
  };

  return (
    <div>
      {/* Confirm dialog */}
      {confirming && (
        <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 10, padding: "14px 18px", marginBottom: 12 }}>
          <p style={{ color: "#fca5a5", fontSize: 13, margin: "0 0 10px" }}>
            {confirming.type === "totp" && `¿Resetear TOTP de ${confirming.email}? Deberá configurar autenticación nuevamente.`}
            {confirming.type === "devices" && `¿Revocar todos los dispositivos de ${confirming.email}?`}
            {confirming.type === "role" && `¿Cambiar rol de ${confirming.email} a ${confirming.newRole}?`}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => {
              if (confirming.type === "totp") resetTOTP(confirming.id, confirming.email);
              else if (confirming.type === "devices") revokeDevices(confirming.id, confirming.email);
              else if (confirming.type === "role") changeRole(confirming.id, confirming.email, confirming.newRole);
            }} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#dc2626", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Confirmar
            </button>
            <button onClick={() => setConfirming(null)} style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${D.bdr}`, background: "transparent", color: "#94a3b8", fontSize: 12, cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button onClick={() => setTab("pending")} style={btnStyle(tab === "pending")}>
          Pendientes {pending.length > 0 && <span style={{ background: "#dc2626", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 11, marginLeft: 6 }}>{pending.length}</span>}
        </button>
        <button onClick={() => setTab("approved")} style={btnStyle(tab === "approved")}>Aprobados ({all.length})</button>
      </div>

      {loading && <p style={{ color: "#64748b", fontSize: 14, textAlign: "center" }}>Cargando...</p>}

      {!loading && tab === "pending" && (pending.length === 0
        ? <DC><p style={{ color: "#64748b", fontSize: 13, textAlign: "center" }}>No hay solicitudes pendientes</p></DC>
        : pending.map(d => <DoctorCard key={d.id} d={d} showActions />)
      )}

      {!loading && tab === "approved" && (all.length === 0
        ? <DC><p style={{ color: "#64748b", fontSize: 13, textAlign: "center" }}>No hay médicos aprobados</p></DC>
        : all.map(d => <DoctorCard key={d.id} d={d} showActions />)
      )}
    </div>
  );
}
